import { Box, Grid } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import {
  drawerListItems,
  PortalAdminPageName,
  PortalAdminPages,
  portalAdminDrawerSections,
} from '@pages/PortalAdmin/PortalAdmin.constants';
import { Layout } from '@layout/Layout';
import { ThemeProvider } from '@mui/material/styles';
import composeTheme from '@theme/theme';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { AdminDrawer } from '@components/drawer/AdminDrawer';
import React, { useCallback, Suspense, useState } from 'react';
import { FullscreenLoading } from '@components/Loading';
import { AdvancedPortalDataSideBar } from '@pages/PortalAdmin/sidebarItems/advancedPortalData/AdvancedPortalDataSideBar';
import { portalAdminContentMaxWidth } from '@pages/PortalAdmin/PortalAdmin.styles';

type PortalAdminDrilldown = 'advanced-portal-configuration';

const getPortalAdminDrilldown = (pathname: string): PortalAdminDrilldown | null => {
  if (pathname.includes(PortalAdminPages[PortalAdminPageName.AdvancedPortalData].url)) {
    return 'advanced-portal-configuration';
  }

  return null;
};

// Custom loading component for admin portal that fills the content area
const AdminContentLoading = () => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        width: '100vw',
      }}
    >
      <FullscreenLoading />
    </Box>
  );
};

// Memoized main content component to prevent unnecessary re-renders
const MainContent = React.memo(() => (
  <Box
    component='main'
    sx={{
      flexGrow: 1,
      padding: { xs: 1, sm: 2 },
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxSizing: 'border-box',
      width: '100%',
    }}
  >
    <Suspense fallback={<AdminContentLoading />}>
      <Grid
        data-testid='parent-grid'
        container
        style={{
          marginTop: 0,
        }}
        spacing={3}
        sx={{
          width: '100%',
          maxWidth: portalAdminContentMaxWidth,
          flexDirection: 'column',
          paddingTop: 0,
          alignItems: 'stretch',
        }}
      >
        <Outlet />
      </Grid>
    </Suspense>
  </Box>
));

MainContent.displayName = 'MainContent';

export const PortalAdminLayout = () => {
  const location = useLocation();
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const [activeDrilldown, setActiveDrilldown] = useState<PortalAdminDrilldown | null>(() =>
    getPortalAdminDrilldown(location.pathname),
  );

  // Re-derive the drilldown when the route changes (adjusting state during render, not in an effect).
  const [drilldownPath, setDrilldownPath] = useState(location.pathname);
  if (drilldownPath !== location.pathname) {
    setDrilldownPath(location.pathname);
    setActiveDrilldown(getPortalAdminDrilldown(location.pathname));
  }

  const handleToggleDrawer = useCallback((newName: string | null) => {
    const selected = drawerListItems.find((item) => item.name === newName);
    const selectedPath = selected?.path ?? '';

    // Navigation itself is handled by the AdminDrawer item's own link (`to`); this just primes
    // the drilldown state immediately instead of waiting for the location-driven effect below.
    setActiveDrilldown(getPortalAdminDrilldown(selectedPath));
  }, []);

  const adminDrawerContent =
    activeDrilldown === 'advanced-portal-configuration' ? (
      <AdvancedPortalDataSideBar
        onBack={() => setActiveDrilldown(null)}
        onClose={(e: React.MouseEvent) => e.stopPropagation()}
      />
    ) : null;

  if (isMobileOrSmallScreen) {
    // Mobile Portal Admin navigation lives in the header hamburger menu.
    return (
      <ThemeProvider theme={composeTheme('light')}>
        <Layout title='Portal Admin'>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Grid
              data-testid='parent-grid'
              container
              style={{
                marginTop: 3,
              }}
              spacing={3}
              sx={{
                width: '100%',
                maxWidth: portalAdminContentMaxWidth,
                flexDirection: 'column',
                paddingTop: 1,
                alignItems: 'stretch',
              }}
            >
              <Outlet />
            </Grid>
          </Box>
        </Layout>
      </ThemeProvider>
    );
  }

  // Desktop layout with custom admin drawer
  return (
    <ThemeProvider theme={composeTheme('light')}>
      <Layout title='Portal Admin' config={{ padding: 0 }}>
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {/* Admin Navigation */}
          <AdminDrawer sections={portalAdminDrawerSections} toggleDrawer={handleToggleDrawer}>
            {adminDrawerContent}
          </AdminDrawer>

          {/* Main content area */}
          <MainContent />
        </Box>
      </Layout>
    </ThemeProvider>
  );
};
